import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SendIcon from '@mui/icons-material/Send';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/pt-br';

dayjs.extend(customParseFormat);

const FORMAT = 'DD/MM/YYYY HH:mm';

interface ChatDateTimeInputProps {
  disabled: boolean;
  onSend: (value: string) => void;
}

function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  let result = '';
  for (let i = 0; i < digits.length && i < 14; i++) {
    if (i === 2 || i === 4) result += '/';
    else if (i === 6) result += ' ';
    else if (i === 8 || i === 10) result += ':';
    result += digits[i];
  }
  return result;
}

export default function ChatDateTimeInput({ disabled, onSend }: ChatDateTimeInputProps) {
  const [value, setValue] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const parsedDayjs: Dayjs | null = value ? dayjs(value, FORMAT, true) : null;
  const pickerValue: Dayjs | null = parsedDayjs && parsedDayjs.isValid() ? parsedDayjs : null;

  const isValid = parsedDayjs?.isValid() ?? false;

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(applyMask(e.target.value));
  };

  const handlePickerChange = (newVal: Dayjs | null) => {
    if (newVal && newVal.isValid()) {
      setValue(newVal.format(FORMAT));
    }
    setPickerOpen(false);
  };

  const handleSend = () => {
    if (disabled || !isValid) return;
    const val = value.trim();
    setValue('');
    onSend(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', width: '100%' }}>
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Aguarde...' : 'dd/mm/yyyy hh:mm'}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  tabIndex={-1}
                  disabled={disabled}
                  onClick={() => setPickerOpen(true)}
                  sx={{ color: 'action.active' }}
                >
                  <CalendarMonthOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.875rem',
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={disabled || !isValid}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            width: 36,
            height: 36,
            flexShrink: 0,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DateTimePicker
        open={pickerOpen}
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        value={pickerValue}
        onChange={handlePickerChange}
        format={FORMAT}
        ampm={false}
        views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
        slotProps={{
          textField: { sx: { display: 'none' } },
          popper: { placement: 'top-end' },
        }}
      />
    </LocalizationProvider>
  );
}
